import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Badge } from "@/shared/components/ui/badge";
import { SafeText } from "@/shared/components/SafeText";
import { formatDateShort } from "@/shared/lib/format-date";
import { MemberActions } from "./MemberActions";
import {
  availableMemberActions,
  type MemberAction,
  type MemberPermissions,
} from "./member-actions";
import { MemberLocations } from "./MemberLocations";
import type { AdminOrganizationMember } from "./types";
import { userPath } from "@/shared/users/user-link";

interface MembersTableProps {
  readonly items: readonly AdminOrganizationMember[];
  /** What the viewer may do; a row only offers the actions allowed on its member. */
  readonly permissions?: MemberPermissions;
  readonly onAction?: (action: MemberAction, member: AdminOrganizationMember) => void;
}

const READ_ONLY: MemberPermissions = { manageAdmins: false, manageMembers: false };

const TH_CLASS =
  "px-3 py-2 text-left font-sans text-xs font-medium uppercase tracking-wide text-muted-foreground";
const TD_CLASS = "px-3 py-3 align-top font-sans text-sm text-foreground";
const COLUMNS = ["name", "role", "status", "locations", "joined"] as const;
const ACTIONS_COLUMN_KEY = "organizationAccess.memberActions.label";

/**
 * The roster of one organization: display names only. The API omits emails and
 * phones, and nothing here asks for them; the member's user page has the rest.
 */
export function MembersTable({ items, permissions = READ_ONLY, onAction }: MembersTableProps) {
  const { t, i18n } = useTranslation();
  // The column exists for viewers who may act on someone; read-only viewers keep the plain roster.
  const showActions =
    onAction !== undefined && (permissions.manageAdmins || permissions.manageMembers);

  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <table data-testid="members-table" className="w-full border-collapse">
        <caption className="sr-only">{t("organizations.members.table.caption")}</caption>
        <thead className="border-b border-border bg-muted/30">
          <tr>
            {COLUMNS.map((column) => (
              <th key={column} scope="col" className={TH_CLASS}>
                {t(`organizations.members.table.${column}`)}
              </th>
            ))}
            {showActions && (
              <th scope="col" className={TH_CLASS}>
                <span className="sr-only">{t(ACTIONS_COLUMN_KEY)}</span>
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {items.map((m) => (
            <tr
              key={m.membershipId}
              data-testid={`member-row-${m.membershipId}`}
              className="border-b border-border last:border-0 hover:bg-muted/20"
            >
              <th scope="row" className={`${TD_CLASS} text-left`}>
                <Link
                  to={userPath(m.user.id)}
                  className="break-words font-medium text-primary underline-offset-2 hover:underline"
                >
                  <SafeText fallback={t("organizations.unnamedUser")}>
                    {m.user.displayName}
                  </SafeText>
                </Link>
              </th>
              <td className={TD_CLASS}>
                <Badge variant="outline">{t(`organizations.members.role.${m.role}`)}</Badge>
              </td>
              <td className={TD_CLASS}>
                <Badge
                  variant={m.status === "ACTIVE" ? "default" : "secondary"}
                  data-testid={`member-status-${m.membershipId}`}
                >
                  {t(`organizations.members.status.${m.status}`)}
                </Badge>
              </td>
              <td className={TD_CLASS}>
                <MemberLocations member={m} />
              </td>
              <td className={`${TD_CLASS} whitespace-nowrap text-muted-foreground`}>
                {formatDateShort(m.createdAt, i18n.language) || "—"}
              </td>
              {showActions && (
                <td className={`${TD_CLASS} text-right`}>
                  <MemberActions
                    membershipId={m.membershipId}
                    memberName={m.user.displayName}
                    actions={availableMemberActions(m, permissions)}
                    onAction={(action) => onAction(action, m)}
                  />
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
